import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, getDocs, doc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Customer, CustomerRelationshipLog } from '../types/customer';
import { ClockIcon, UserIcon, DocumentArrowDownIcon, MagnifyingGlassIcon, PencilIcon, PlusIcon, FunnelIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import CustomerRelationshipLogForm from '../components/customers/CustomerRelationshipLogForm';
import CreateRelationshipLogModal from '../components/customers/CreateRelationshipLogModal';
import EditRelationshipLogModal from '../components/customers/EditRelationshipLogModal';
import { userService } from '../services/userService';


interface LogEntryWithCustomer extends CustomerRelationshipLog {
  customerName: string;
  customerType: string;
  customerTaxNumber?: string;
}

const CustomerRelationshipLogs: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogEntryWithCustomer[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntryWithCustomer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [filterOpenOnly, setFilterOpenOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingLog, setEditingLog] = useState<LogEntryWithCustomer | null>(null);
  const [customersMap, setCustomersMap] = useState<Map<string, Customer>>(new Map());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Load user names for displaying in logs
  const loadUserNames = useCallback(async (logs: LogEntryWithCustomer[]) => {
    const uniqueUserIds = Array.from(new Set(logs.map(l => l.createdBy).filter(Boolean)));
    const newUserNames: Record<string, string> = {};

    for (const userId of uniqueUserIds) {
      if (!userNames[userId] && !userId.includes('@')) { // Skip if already loaded or if it's an email
        try {
          const userData = await userService.getUser(userId);
          if (userData) {
            newUserNames[userId] = userData.displayName || userData.email || 'Ismeretlen felhasználó';
          } else {
            newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
        }
      }
    }

    if (Object.keys(newUserNames).length > 0) {
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    }
  }, [userNames]);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const allLogs: LogEntryWithCustomer[] = [];
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          if (customerData.relationshipLogs && customerData.relationshipLogs.length > 0) {
            const customerName = customerData.type === 'organization'
              ? customerData.companyName
              : `${customerData.firstName} ${customerData.lastName}`;
              
            customerData.relationshipLogs.forEach(log => {
              allLogs.push({
                ...log,
                customerName,
                customerType: customerData.type,
                customerTaxNumber: customerData.taxNumber
              });
            });
          }
        });
        
        // Sort logs by creation date (newest first)
        const sortedLogs = allLogs.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
          const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
          return dateB.getTime() - dateA.getTime();
        });
        
        setLogs(sortedLogs);
        setFilteredLogs(sortedLogs);
        
        // Load user names after logs are loaded
        if (sortedLogs.length > 0) {
          loadUserNames(sortedLogs);
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [loadUserNames]);

  useEffect(() => {
    // Apply filters when searchTerm, dateFilter, or filterOpenOnly changes
    let filtered = [...logs];
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.customerName.toLowerCase().includes(term) || 
        log.content.toLowerCase().includes(term) || 
        log.entryId.toLowerCase().includes(term) ||
        (log.customerTaxNumber && log.customerTaxNumber.toLowerCase().includes(term))
      );
    }
    
    if (dateFilter) {
      filtered = filtered.filter(log => {
        const logDate = log.createdAt instanceof Date 
          ? log.createdAt 
          : log.createdAt.toDate();
          
        return logDate.toISOString().substring(0, 10) === dateFilter;
      });
    }
    
    // Apply the open tasks filter
    if (filterOpenOnly) {
      filtered = filtered.filter(log => log.isOpen);
    }
    
    setFilteredLogs(filtered);
  }, [logs, searchTerm, dateFilter, filterOpenOnly]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const customersMapTemp = new Map<string, Customer>();
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          customersMapTemp.set(doc.id, customerData);
        });
        
        setCustomersMap(customersMapTemp);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    
    fetchCustomers();
  }, []);

  // Refresh customers map whenever logs are refreshed
  useEffect(() => {
    const updateCustomersMap = async () => {
      try {
        const customersSnapshot = await getDocs(collection(db, 'customers'));
        const customersMapTemp = new Map<string, Customer>();
        
        customersSnapshot.forEach((doc) => {
          const customerData = doc.data() as Customer;
          customersMapTemp.set(doc.id, customerData);
        });
        
        setCustomersMap(customersMapTemp);
      } catch (error) {
        console.error('Error updating customers map:', error);
      }
    };
    
    if (!loading) {
      updateCustomersMap();
    }
  }, [logs, loading]);

  const refreshLogs = async () => {
    setLoading(true);
    try {
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const allLogs: LogEntryWithCustomer[] = [];
      
      customersSnapshot.forEach((doc) => {
        const customerData = doc.data() as Customer;
        if (customerData.relationshipLogs && customerData.relationshipLogs.length > 0) {
          const customerName = customerData.type === 'organization'
            ? customerData.companyName
            : `${customerData.firstName} ${customerData.lastName}`;
            
          customerData.relationshipLogs.forEach(log => {
            allLogs.push({
              ...log,
              customerName,
              customerType: customerData.type,
              customerTaxNumber: customerData.taxNumber
            });
          });
        }
      });
      
      // Sort logs by creation date (newest first)
      const sortedLogs = allLogs.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
        const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
        return dateB.getTime() - dateA.getTime();
      });
      
      setLogs(sortedLogs);
      setFilteredLogs(sortedLogs);
    } catch (error) {
      console.error('Error refreshing logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Prepare data for export
    const exportData = filteredLogs.map(log => {
      const createdAt = log.createdAt instanceof Date 
        ? log.createdAt 
        : log.createdAt.toDate();
        
      return {
        'Entry ID': log.entryId,
        'Customer': log.customerName,
        'Type': log.customerType === 'organization' ? 'Organization' : 'Individual',
        'Tax Number': log.customerTaxNumber || 'N/A',
        'Content': log.content,
        'Status': log.isOpen ? 'Open' : 'Closed',
        'Created At': createdAt.toLocaleString('hu-HU'),
        'Created By': log.createdByName || userNames[log.createdBy] || log.createdBy
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RelationshipLogs');
    
    // Export the file
    XLSX.writeFile(workbook, 'CustomerRelationshipLogs.xlsx');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('customers.relationshipLog.title') || 'Customer Relationship Logs'}
        </h1>
        
        <div className="mt-3 sm:mt-0 flex space-x-3">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('customers.relationshipLog.add') || 'Add new log entry'}
          </button>
          
          <button
            onClick={exportToExcel}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {t('common.exportExcel') || 'Export to Excel'}
          </button>
        </div>
      </div>
      
      {/* Edit Log Modal */}
      {editingLog && (
        <EditRelationshipLogModal
          isOpen={true}
          onClose={() => setEditingLog(null)}
          onSuccess={refreshLogs}
          logToEdit={editingLog}
          customer={customersMap.get(editingLog.customerId) || { id: editingLog.customerId } as Customer}
        />
      )}
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.search') || 'Search'}
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                placeholder={t('customers.relationshipLog.searchPlaceholder') || 'Search by customer, content, or ID...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.dateFilter') || 'Filter by Date'}
            </label>
            <input
              type="date"
              id="date-filter"
              className="focus:ring-primary-500 focus:border-primary-500 block w-full py-2 sm:text-sm border-gray-300 rounded-md"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('customers.relationshipLog.statusFilter') || 'Filter by Status'}
            </label>
            <button
              type="button"
              onClick={() => setFilterOpenOnly(!filterOpenOnly)}
              className={`inline-flex items-center w-full px-4 py-2 border text-sm rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                filterOpenOnly 
                  ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-500' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500'
              }`}
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              {t('customers.relationshipLog.filterOpenOnly') || 'Open tasks only'}
              {filterOpenOnly && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">
                  {filteredLogs.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Results count */}
      <div className="text-sm text-gray-600 mb-4">
        {t('common.showing', { count: filteredLogs.length }) || `Showing ${filteredLogs.length} entries`}
        {filteredLogs.length < logs.length && ` (filtered from ${logs.length})`}
      </div>
      
      {/* Logs list */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">{t('common.loading') || 'Loading...'}</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            {searchTerm || dateFilter || filterOpenOnly
              ? (t('customers.relationshipLog.noResultsFilter') || 'No logs match your filters')
              : (t('customers.relationshipLog.noLogs') || 'No relationship logs found')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map((log) => {
            const createdAt = log.createdAt instanceof Date 
              ? log.createdAt 
              : log.createdAt.toDate();
              
            const formattedDate = new Intl.DateTimeFormat('hu-HU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(createdAt);

            const isCurrentUserCreator = user && log.createdBy === user.uid;

            return (
              <div key={log.id} className={`bg-white rounded-lg shadow overflow-hidden ${
                log.isOpen ? 'border border-amber-200' : 'border border-green-200'
              }`}>
                <div className={`px-4 py-3 border-b ${
                  log.isOpen 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="font-medium text-primary-700">{log.entryId}</div>
                    <div className="flex items-center text-sm text-gray-600">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-500" />
                      <span>{formattedDate}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap justify-between gap-x-4 gap-y-1">
                    <div className="flex flex-wrap gap-x-4 items-center">
                      <div className="text-sm text-gray-800">
                        <span className="font-medium">{t('customers.fields.name') || 'Customer'}:</span> {log.customerName}
                      </div>
                      {log.customerTaxNumber && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{t('customers.fields.taxNumber') || 'Tax Number'}:</span> {log.customerTaxNumber}
                        </div>
                      )}
                      <div className="text-sm text-gray-600">
                        <UserIcon className="h-4 w-4 mr-1 inline text-gray-500" />
                        <span>{log.createdByName || userNames[log.createdBy] || log.createdBy}</span>
                      </div>
                      
                      {/* Status indicator */}
                      <div className={`flex items-center text-sm ${
                        log.isOpen ? 'text-amber-700' : 'text-green-700'
                      }`}>
                        {log.isOpen ? (
                          <>
                            <ExclamationCircleIcon className="h-4 w-4 mr-1 text-amber-500" />
                            <span>{t('customers.relationshipLog.statusOpen') || 'Open'}</span>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
                            <span>{t('customers.relationshipLog.statusClosed') || 'Closed'}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Only show edit button, not delete */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingLog(log)}
                        disabled={isSubmitting}
                        className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                        title={t('customers.relationshipLog.edit') || 'Edit log entry'}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`p-4 overflow-hidden ${!log.isOpen ? 'bg-green-50' : 'bg-white'}`}>
                  <p className="text-gray-800 whitespace-pre-wrap break-words w-full max-w-full overflow-hidden overflow-wrap-anywhere hyphens-auto" style={{ wordBreak: 'break-word', textOverflow: 'ellipsis' }}>{log.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Create Relationship Log Modal */}
      <CreateRelationshipLogModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={refreshLogs}
      />
    </div>
  );
};

export default CustomerRelationshipLogs; 